import {
  convert24To12,
  WEEKDAYS,
} from '@mr-yum/frontend-core/dist/support/dates'
import {
  ChevronDownIcon,
  cn,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  InfoFilledIcon,
} from '@mr-yum/frontend-ui'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { OrderingType, TimeSlot, VenueLandingQuery } from '@/gql/graphql'

import { getMenuAvailability, getNameForOrderingType } from './utils/venueUtils'

interface Props {
  venue: NonNullable<VenueLandingQuery['guestVenue']>
  orderingType: OrderingType
}

export function OrderingUnavailable({ venue, orderingType }: Props) {
  const [open, setOpen] = useState(false)
  const intl = useIntl()

  const { menuStatus } = getMenuAvailability(venue, orderingType)
  const isAcceptingOrders = menuStatus === 'open'
  if (isAcceptingOrders) {
    return null
  }

  const outOfHours = menuStatus === 'outOfHours'
  // Note: out of hours already takes precedence over orderingTypeUnavailable
  const isOrderingTypeUnavailable = menuStatus === 'orderingTypeUnavailable'

  const heading = isOrderingTypeUnavailable
    ? `${getNameForOrderingType(orderingType, intl)} is currently unavailable`
    : 'Digital ordering is unavailable'

  const message = isOrderingTypeUnavailable
    ? 'Please speak to a staff member for assistance.'
    : outOfHours
      ? 'Digital ordering is only available during the following hours.'
      : 'Digital ordering is temporarily unavailable. Please talk to a member of our staff.'

  const showCollapsible = !isOrderingTypeUnavailable && outOfHours

  const handleOpenChange = (open: boolean) => {
    if (outOfHours) {
      setOpen(open)
    }
  }

  return (
    <CollapsibleRoot open={open} onOpenChange={handleOpenChange}>
      <div className="px-4 pt-4">
        <CollapsibleTrigger asChild>
          <div className="flex gap-4 rounded-lg bg-surface-bright px-4 pb-5 pt-4">
            <InfoFilledIcon className="h-5 w-5 flex-shrink-0 text-semantic-info-foreground-bold" />
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <h1 className="my-label-md">{heading}</h1>
                <p className="my-body-sm">{message}</p>
              </div>
              {showCollapsible && (
                <div>
                  <div className="flex justify-between border-t pt-4">
                    <p className="my-body-sm-bold">View hours</p>
                    <ChevronDownIcon
                      className={cn(
                        'relative top-[1px] ml-1 h-5 w-5 transition-all duration-300 ease-in-out',
                        { 'rotate-180': open },
                      )}
                    />
                  </div>
                  <CollapsibleContent>
                    <div className="mt-2 flex flex-col gap-1 my-body-sm">
                      {Object.entries(WEEKDAYS).map(([key, day]) => {
                        const dayHours =
                          venue.hours?.[key as keyof typeof WEEKDAYS] || []
                        return (
                          <div key={key} className="flex">
                            <div className="flex-1">{day}</div>
                            <div className="flex-1 text-left">
                              {dayHours.length > 0 ? (
                                dayHours.map(
                                  (hours: TimeSlot, index: number) => (
                                    <div key={index}>
                                      {`${convert24To12(hours.startTime)} — ${convert24To12(hours.endTime)}`}
                                    </div>
                                  ),
                                )
                              ) : (
                                <div>Closed</div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </CollapsibleContent>
                </div>
              )}
            </div>
          </div>
        </CollapsibleTrigger>
      </div>
    </CollapsibleRoot>
  )
}
